/* WHICH spells get a target picker, who they are aimed at, and how far away.
 *
 * The REACH itself is not here: auraReach.tokensInside already answers "who is
 * within N feet of this token", and a first version of this file reimplemented it
 * formula for formula. What is here is the part nothing else answered — whether a
 * spell targets creatures at all, who it is aimed at, and the distance to say so.
 *
 * Original note, still true:
 * Who can this spell reach, and how many of them may it pick?
 *
 * Healing Word offered no in-range list while Dissonant Whispers did (reported
 * 2026-09-28). The cause was eligibility, not geometry: the engine admits a spell
 * only when it has an AREA, and Dissonant Whispers happened to be in the DM's
 * inventory marked as a burst. A spell that simply targets a creature at a range
 * had no way in, however ordinary.
 *
 * The DM's own design, and it needs no new maths: a range-targeted spell is an
 * emanation whose radius is its range. areaOf already centres a linked effect on
 * its caster's token and measures from the token's EDGE, which is Roll20's own
 * rule for auras — so the candidate list is the emanation rule, unchanged. What
 * differs is only what happens next: an emanation hits everyone inside, and this
 * lets the DM pick, up to the number the spell allows.
 */
import { cellOf, squareCentres, type MapToken, type PageGeometry } from "./auraReach";

/** The circle a spell's range covers, centred on the caster, measured from the token's edge. */

/* HOW MANY THE SPELL MAY PICK.
 *
 * D&D Beyond's spell data carries range, area, save, damage and concentration —
 * but NOT a target count, so there is nothing to derive this from. A short table,
 * then, kept to spells whose count is a rule rather than a judgement, with the
 * per-slot growth where the spell has it.
 *
 * `base` targets at the spell's own level, plus `perSlot` for each level above it.
 * A spell not listed allows one, which is the common case and the safe one: the
 * picker treats this as GUIDANCE and does not forbid a DM who knows better.
 */
/* WHO A SPELL IS AIMED AT.

   The picker shipped without this and was wrong for it: Healing Word offered the
   bandits and Hold Person offered your own cleric. Every target list needs to know
   the intent, and the combat page already holds the other half — an ally/foe toggle
   per row (the handshake button), which is the neutral-or-friendly distinction the
   DM has already made by hand.

     ally    only allies and neutrals: healing, buffs, protection
     enemy   only foes: saves, attacks, debuffs
     any     either, and the spell or the DM decides
     self    the caster alone

   "any" is the honest answer for a spell nobody has classified, not a guess at one
   of the other three. */
export type Intent = "ally" | "enemy" | "any" | "self";

/* SPELLS WHOSE AIM IS A RULE, not a guess from their tags.

   This was a table of target COUNTS as well, and those are gone. spellTargets.ts
   opens by arguing against exactly that — "a table would be another pile of rules
   data to get wrong, and this session has twice shown that wrong rules data is
   worse than none" — and it was right: the counts duplicated what a spell's own
   text states, targetCapFrom and targetGrowthFrom now read both the base and the
   per-slot growth from that text, and nothing in the app was calling the table's
   counts any more.

   What remains is the one thing no source provides. D&D Beyond's tags classify most
   spells (Healing, Buff, Debuff) and intentFromTags reads them; these are the few
   where the tags are absent or misleading, and a wrong aim offers a heal to the
   bandits. Short on purpose. */
export const SPELL_INTENT: Record<string, Intent> = {
  "bless": "ally",
  "bane": "enemy",
  "aid": "ally",
  "command": "enemy",
  "charm person": "enemy",
  "hold person": "enemy",
  "mass healing word": "ally",
  // Healing Word and Cure Wounds scale their DICE, not their targets - a distinction
  // worth pinning, because "upcast" reads as "more of everything" and is not.
  "healing word": "ally",
  "cure wounds": "ally",
};

const key = (name: string) => String(name || "").replace(/\s+/g, " ").trim().toLowerCase();



/** The creatures a spell at this range could pick: everyone whose occupied square
 *  centre falls inside the range circle. The caster is excluded by default — a
 *  spell that may target its caster says so, and offering yourself for Hold Person
 *  is noise — but `includeSelf` puts them back for Cure Wounds and the like. */

/* The intent of a spell the table has not classified.

   Derived from what D&D Beyond does carry, in the order the signals are worth
   trusting. A SAVE is the strongest: a spell you force a save against is aimed at
   someone you are not helping. Healing is next. Damage without a save is still
   aimed at a foe. Everything else is genuinely unknown, and says so — the picker
   shows "any" as unclassified rather than dressing it as a decision. */
/* The tag-less entry point into the same rule.

   Kept because it works and callers use it, but delegating rather than repeating:
   two intent rules drifting apart is exactly the fault that cost three fixes on
   2026-09-28, and this one decides who gets healed. */
export function intentOf(spell: { name: string; save?: string; healing?: boolean; damage?: boolean; self?: boolean }): { intent: Intent; known: boolean } {
  return intentFromTags({ ...spell, tags: [] });
}

/** Does this candidate suit the spell's intent? `ally` covers neutrals too: the
 *  combat page's toggle is friendly-or-not, and a neutral NPC the DM has marked
 *  friendly is a legitimate target for a heal. */
/* Does this candidate suit the spell's intent?

   `isAlly` is a TRISTATE, and that matters more than it looks. Ally-ness is read
   from the combat page's handshake toggle, which lives on a tracker row — so a
   token on the map with no row has no answer, not a negative one. Treating unknown
   as "foe" meant that clearing the initiative made Healing Word filter to allies
   and offer NOBODY: correct logic, useless result, and no way for the DM to tell
   why. Unknown suits everything and is labelled instead.

   `ally` covers neutrals: the toggle is friendly-or-not, and a neutral the DM has
   marked friendly is a legitimate target for a heal. */
export function suitsIntent(intent: Intent, cand: { isAlly: boolean | null; isCaster: boolean }): boolean {
  if (intent === "self") return cand.isCaster;
  if (cand.isAlly === null) return true;
  if (intent === "ally") return cand.isAlly;
  if (intent === "enemy") return !cand.isAlly;
  return true;
}
/* HOW FAR AWAY, in feet, measured the way the range check measures.

   This has to agree with targetsInRange or the list contradicts itself: a target
   at the limit of a 60 ft spell must read "60 ft.", not 65. Both work from the
   caster token's EDGE to the nearest square centre the target occupies, which is
   Roll20's own rule for auras.

   Rounded to the nearest 5 ft because the grid is in 5 ft squares and "17 ft."
   is a false precision about a token's pixel position. */
export function distanceFt(caster: MapToken, target: MapToken, page: PageGeometry | null): number {
  if (target.id === caster.id) return 0;
  const cell = cellOf(page);
  /* The same primitives tokensInside uses, so the number shown can never disagree
     with whether the token was offered. The first version of this reimplemented the
     maths and was a third copy of one formula. */
  const nearest = Math.min(...squareCentres(target, cell.px).map((q) => Math.hypot(q.x - caster.left, q.y - caster.top)));
  const edge = Math.max(0, nearest - Math.max(caster.width, caster.height) / 2);
  /* Rounded to the grid: "17 ft." is a false precision about a token's pixel
     position, and the table thinks in squares. */
  return Math.round(((edge / cell.px) * cell.feet) / 5) * 5;
}
/* DOES THIS SPELL TARGET CREATURES AT ALL?

   Audited against the DM's own account on 2026-09-29: of 831 unique spells, 483
   reached the picker, and a large slice of those target no creature — Mending,
   Knock, Mage Hand, Dancing Lights, Magic Mouth, Identify, Dimension Door. The
   picker would have opened for Knock and offered the bandits.

   So the default is INVERTED. It used to open unless something stopped it; now it
   stays shut unless a creature-targeting signal is present. An unwanted dialog for
   a door is a worse failure than a missing one for an obscure spell, because the
   first happens in every session and teaches the DM to dismiss the thing.

   D&D Beyond's own tags carry this better than any name matching: a spell tagged
   Healing, Buff or Warding is cast on somebody; Damage and Debuff are cast at
   somebody; Utility, Detection, Communication, Teleportation, Creation, Scrying
   and Exploration are, on their own, cast on the world. A save or damage dice
   settle it regardless of tag. */
const CREATURE_TAGS = new Set(["Healing", "Buff", "Warding", "Debuff", "Curse", "Charmed", "Compulsion", "Banishment", "Damage", "Biomancy", "Sangromancy"]);
/* Tags that describe acting on the world rather than on a creature. Not a refusal
   on their own — Feather Fall is Utility and lands on creatures — only the absence
   of any creature signal is. */
const WORLD_TAGS = new Set(["Utility", "Detection", "Communication", "Teleportation", "Creation", "Scrying", "Exploration", "Foreknowledge", "Deception", "Environment", "Social", "Movement", "Control", "Summoning", "Illumination", "Negation"]);

export interface Targeting { targetsCreatures: boolean; why: string }
export function targetsCreatures(spell: { name: string; save?: string; damage?: boolean; tags?: string[] }): Targeting {
  if (SPELL_INTENT[key(spell.name)]) return { targetsCreatures: true, why: "on record" };
  /* A save is somebody resisting: only a creature does that. */
  if (spell.save) return { targetsCreatures: true, why: "it forces a saving throw" };
  const tags = spell.tags || [];
  const creature = tags.filter((t) => CREATURE_TAGS.has(t));
  if (creature.length) return { targetsCreatures: true, why: `tagged ${creature.join(", ")}` };
  if (spell.damage) return { targetsCreatures: true, why: "it rolls damage" };
  const world = tags.filter((t) => WORLD_TAGS.has(t));
  if (world.length) return { targetsCreatures: false, why: `tagged ${world.join(", ")} and nothing creature-facing` };
  /* No signal either way. Shut, and said out loud: the DM can still place or
     resolve it by hand, and a dialog nobody asked for is the worse error. */
  return { targetsCreatures: false, why: "nothing on record says it targets a creature" };
}

/* Intent from the tags, which the audit showed are worth far more than name
   matching: the tag-aware rule classified 99 spells as ally where the shipped rule
   found 9, and cut the unclassified pile from 375 to 283. Healing first, then a
   buff with nobody resisting it, then a save, then damage. */
const ALLY_TAGS = new Set(["Healing", "Buff", "Warding"]);
const FOE_TAGS = new Set(["Damage", "Debuff", "Curse", "Banishment", "Compulsion", "Charmed"]);
export function intentFromTags(spell: { name: string; save?: string; damage?: boolean; self?: boolean; tags?: string[] }): { intent: Intent; known: boolean } {
  const listed = SPELL_INTENT[key(spell.name)];
  if (listed) return { intent: listed, known: true };
  if (spell.self) return { intent: "self", known: true };
  const tags = spell.tags || [];
  if (tags.includes("Healing")) return { intent: "ally", known: true };
  /* A buff with a save is not a buff on a friend — Feign Death carries both Buff
     and Debuff, and the save is the tie-breaker. */
  if (tags.some((t) => ALLY_TAGS.has(t)) && !spell.save) return { intent: "ally", known: true };
  if (spell.save) return { intent: "enemy", known: true };
  /* An explicit healing flag, for callers that have no tags to offer. */
  if ((spell as { healing?: boolean }).healing) return { intent: "ally", known: true };
  if (spell.damage || tags.some((t) => FOE_TAGS.has(t))) return { intent: "enemy", known: true };
  return { intent: "any", known: false };
}